import React, { useEffect, useCallback } from 'react';
import {
  Routes, Route, useLocation, useNavigate, Outlet,
} from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';

import { getUserAuthAction } from './actions/authActions';
import { ERROR_AUTH, USER_AUTH } from './actions/types';
import { SECTIONS } from './constants/majeeRights';

import { setVhUnit } from './utils/utils';

import Toaster from './lib/Toaster/Toaster';

import Index from './pages/Auth/SwitchIndex';
import Login from './pages/Auth/Login';
import SsoLogin from './pages/Auth/SsoLogin';
import Page404 from './pages/Page404/Page404';
import Nav from './components/Nav/Nav';
import RequireRight from './components/RequireRight/RequireRight';
import Home from './pages/Home/Home';
import Actus from './pages/Actus/Actus';
import ActusGestion from './pages/Actus/ActusGestion';
import EditActu from './pages/Actus/EditActu';
import Notifs from './pages/Notifs/Notifs';
import Projects from './pages/Projects/Projects';
import Planning from './pages/Planning/Planning';
import Tools from './pages/Tools/Tools';
import {
  AiresList,
  ProduitsList,
  PathologiesList,
  CentresList,
  ContactsList,
  ContactsDetail,
  CongresList,
  ConcurrentsList,
} from './pages/Content/ContentPages';
import CongressView from './pages/Congres/CongressView';
import CenterView from './pages/Centres/CenterView';
import PathologyView from './pages/Pathologies/PathologyView';
import DrugView from './pages/Produits/DrugView';
import TherapeuticAreaView from './pages/Aires/TherapeuticAreaView';
import CompanyView from './pages/Concurrents/CompanyView';
import ToolView from './pages/Tools/ToolView';

function Layout({ logout }) {
  return (
    <div>
      <Nav logout={logout} />
      <div className="layout-main">
        <Outlet />
      </div>
    </div>
  );
}

export default function index() {
  const { error, userAuth, toast } = useSelector((state) => state.auth);
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const dispatch = useDispatch();
  const token = localStorage.getItem('token');

  const getUser = useCallback(() => {
    getUserAuthAction(dispatch);
  }, [dispatch]);

  function logout() {
    navigate('/login');
    localStorage.removeItem('token');
    sessionStorage.removeItem('majee_sso_attempted');
    dispatch({
      type: ERROR_AUTH,
      payload: null,
    });
    dispatch({
      type: USER_AUTH,
      payload: null,
    });
  }

  useEffect(() => {
    if (token) {
      getUser();
    } else if (!pathname.startsWith('/sso')) {
      navigate('/login');
    }
    setVhUnit();
  }, []);

  useEffect(() => {
    if (pathname !== '/login' && !pathname.startsWith('/sso') && error === 401) {
      logout();
    }
  }, [error, pathname]);

  return (
    <>
      <Routes>
        <Route index element={<Index />} />
        <Route path="login" element={<Login />} />
        <Route path="sso/:token" element={<SsoLogin />} />
        {userAuth
        && (
        <Route path="/majee" element={<Layout logout={() => logout()} />}>
          <Route path="home" element={<Home />} />
          <Route
            path="planning"
            element={(
              <RequireRight section={SECTIONS.PLANNING}>
                <Planning />
              </RequireRight>
            )}
          />
          <Route
            path="actus"
            element={(
              <RequireRight section={SECTIONS.NEWS}>
                <Actus />
              </RequireRight>
            )}
          />
          <Route
            path="actus/gestion"
            element={(
              <RequireRight section={SECTIONS.NEWS} editNews>
                <ActusGestion />
              </RequireRight>
            )}
          />
          <Route
            path="actus/create"
            element={(
              <RequireRight section={SECTIONS.NEWS} editNews>
                <EditActu isCreate />
              </RequireRight>
            )}
          />
          <Route
            path="actus/:id"
            element={(
              <RequireRight section={SECTIONS.NEWS} editNews>
                <EditActu />
              </RequireRight>
            )}
          />
          <Route
            path="project"
            element={(
              <RequireRight section={SECTIONS.PROJECTS}>
                <Projects />
              </RequireRight>
            )}
          />
          <Route
            path="tool"
            element={(
              <RequireRight section={SECTIONS.TOOLS}>
                <Tools />
              </RequireRight>
            )}
          />
          <Route
            path="tool/:id"
            element={(
              <RequireRight section={SECTIONS.TOOLS}>
                <ToolView />
              </RequireRight>
            )}
          />
          <Route
            path="aires"
            element={(
              <RequireRight section={SECTIONS.THERAPEUTIC_AREAS}>
                <AiresList />
              </RequireRight>
            )}
          />
          <Route
            path="aires/:id"
            element={(
              <RequireRight section={SECTIONS.THERAPEUTIC_AREAS}>
                <TherapeuticAreaView />
              </RequireRight>
            )}
          />
          <Route
            path="produits"
            element={(
              <RequireRight section={SECTIONS.PRODUCTS}>
                <ProduitsList />
              </RequireRight>
            )}
          />
          <Route
            path="produits/:id"
            element={(
              <RequireRight section={SECTIONS.PRODUCTS}>
                <DrugView />
              </RequireRight>
            )}
          />
          <Route
            path="pathologies"
            element={(
              <RequireRight section={SECTIONS.PATHOLOGIES}>
                <PathologiesList />
              </RequireRight>
            )}
          />
          <Route
            path="pathologies/:id"
            element={(
              <RequireRight section={SECTIONS.PATHOLOGIES}>
                <PathologyView />
              </RequireRight>
            )}
          />
          <Route
            path="centres"
            element={(
              <RequireRight section={SECTIONS.CENTERS}>
                <CentresList />
              </RequireRight>
            )}
          />
          <Route
            path="centres/:id"
            element={(
              <RequireRight section={SECTIONS.CENTERS}>
                <CenterView />
              </RequireRight>
            )}
          />
          <Route
            path="contacts"
            element={(
              <RequireRight section={SECTIONS.CONTACTS}>
                <ContactsList />
              </RequireRight>
            )}
          />
          <Route
            path="contacts/:id"
            element={(
              <RequireRight section={SECTIONS.CONTACTS}>
                <ContactsDetail />
              </RequireRight>
            )}
          />
          <Route
            path="congres"
            element={(
              <RequireRight section={SECTIONS.CONGRESSES}>
                <CongresList />
              </RequireRight>
            )}
          />
          <Route
            path="congres/:id"
            element={(
              <RequireRight section={SECTIONS.CONGRESSES}>
                <CongressView />
              </RequireRight>
            )}
          />
          <Route
            path="concurrents"
            element={(
              <RequireRight section={SECTIONS.COMPETITORS}>
                <ConcurrentsList />
              </RequireRight>
            )}
          />
          <Route
            path="concurrents/:id"
            element={(
              <RequireRight section={SECTIONS.COMPETITORS}>
                <CompanyView />
              </RequireRight>
            )}
          />
          <Route path="notifications" element={<Notifs />} />
        </Route>
        )}
        <Route path="*" element={<Page404 />} />

      </Routes>
      <Toaster toast={toast} />
    </>
  );
}
